// src/pages/LandingPage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import BackToTopButton from '../components/common/BackToTopButton';
import { Theme, MotionHeadingProps, MotionTextProps } from '../types/theme';
import dumptruckImage from '../assets/images/Dumptruck-Tap-01.webp';
import landingBackgroundImg from '../assets/images/Landing.webp';
import PortfolioGrid from '../components/portfolio/PortfolioGrid';
import AlbumModal from '../components/portfolio/AlbumModal';
import { portfolioAlbums, fetchAlbumDetails } from '../data/portfolioData';
import { PortfolioAlbum, PortfolioState } from '../types/portfolio';

// Props for feature cards
interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

interface StyledProps {
  theme: Theme;
}

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${landingBackgroundImg}) no-repeat center center;
    background-size: cover;
    z-index: -2;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }
`;

const Logo = styled.h1<StyledProps>`
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 2rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin: 1.5rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin: 1rem 0;
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 2rem;
  justify-content: center;
  margin-bottom: 4rem;
  flex-wrap: wrap;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const NavLink = styled(Link)<StyledProps>`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  position: relative;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.4rem 0.8rem;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  z-index: 2;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const FeaturesSection = styled.section<StyledProps>`
  width: 100%;
  padding: 8rem 0;
  position: relative;
  background: rgba(0, 0, 0, 0.7);
`;

const SectionHeading = styled.h2<StyledProps>`
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2.25rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const FeatureCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StyledFeatureCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  backdrop-filter: blur(5px);
`;

const FeatureIcon = styled.div<StyledProps>`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const FeatureTitle = styled.h3<StyledProps>`
  margin-bottom: 1rem;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const FeatureDescription = styled.p<StyledProps>`
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.6;
`;

const ImageContent = styled.div`
  position: relative;
  color: white;
  text-align: center;
  width: 100%;
  padding: 4rem 1rem;
  min-height: calc(100vh - 12rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    min-height: auto;
  }
`;

const ImageHeading = styled(motion.h2)<StyledProps>`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2.25rem;
    margin-bottom: 1rem;
  }
`;

const ImageText = styled(motion.p)<StyledProps>`
  font-size: 2.5rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  line-height: 1.6;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  padding: 0 1rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    line-height: 1.4;
    margin-bottom: 1.2rem;
  }
`;

const MissionSection = styled.section<StyledProps>`
  width: 100%;
  padding: 8rem 0;
  text-align: center;
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const MissionText = styled(motion.p)<StyledProps & MotionTextProps>`
  font-size: 1.4rem;
  max-width: 900px;
  margin: 0 auto;
  line-height: 2;
  color: white;
  font-weight: 300;
  letter-spacing: 0.5px;
  padding: 0 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    line-height: 1.8;
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    line-height: 1.7;
    padding: 0 1rem;
    letter-spacing: 0.3px;
  }
`;

const MissionHeading = styled(SectionHeading)`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  font-weight: 400;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PortfolioSection = styled.section`
  width: 100%;
  padding: 8rem 0;
  position: relative;
  background: rgba(0, 0, 0, 0.7);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PortfolioHeading = styled(motion.h2)<MotionHeadingProps>`
  text-align: center;
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PortfolioDescription = styled(motion.p)<MotionTextProps>`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  font-size: 1.2rem;
  color: white;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
`;

const CTASection = styled.section`
  width: 100%;
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  padding: 8rem 0;
  
  @media (max-width: 768px) {
    min-height: auto;
    padding: 6rem 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${dumptruckImage}) no-repeat center center;
    background-size: cover;
    opacity: 0.8;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    z-index: -1;
  }
`;

const CTAContent = styled(ImageContent)`
  min-height: auto;
  padding: 4rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

// Animation variants for staggered animations
const fadeIn: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Feature cards data
const featureCards: FeatureCardProps[] = [
  {
    icon: '🏗️',
    title: 'Field & Project Management Solutions',
    description: 'We provide comprehensive data-driven results, customized company architecture and consultation, field enhancements and tools, document handling, and much more... We\'re blending years of industry expertise with state of the art intelligence to create higher quality AND more profitable construction companies.'
  },
  {
    icon: '💻',
    title: 'Software and Hardware Development',
    description: 'Currently building customizable and unique solutions to empower a new generation of builders. We offer critical on-site tools, a powerhouse of in-office solutions, web services that showcase your project quality, and an elevated connection with your partners and clients utilizing effective marketing strategies and support.'
  },
  {
    icon: '💡',
    title: 'Fresh Approaches',
    description: 'We\'re bringing a fresh set of eyes to a hardened team of professionals with the full power of AI technologies to implement creative approaches and solve deep-seated issues across industries. Unbound by obsolete practices but humbly grounded in their battle-tested experience.'
  }
];

const LandingPage: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [portfolioState, setPortfolioState] = useState<PortfolioState>({
    selectedAlbum: null,
    isModalOpen: false,
    isLoading: false,
    error: null
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAlbumSelect = async (album: PortfolioAlbum) => {
    setPortfolioState(prev => ({
      ...prev,
      selectedAlbum: album,
      isModalOpen: true,
      isLoading: true,
      error: null
    }));

    try {
      const albumDetails = await fetchAlbumDetails(album.id);
      setPortfolioState(prev => ({
        ...prev,
        selectedAlbum: albumDetails,
        isLoading: false
      }));
    } catch (error) {
      setPortfolioState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load album details',
        isLoading: false
      }));
    }
  };

  const handleCloseModal = () => {
    setPortfolioState(prev => ({
      ...prev,
      isModalOpen: false,
      selectedAlbum: null,
      error: null
    }));
  };

  return (
    <>
      <PageContainer>
        <Logo>TapStructures</Logo>
        <Navigation>
          <NavLink to="/">HOME</NavLink>
          <NavLink to="/about">ABOUT ME</NavLink>
          <NavLink to="/services">SERVICES</NavLink>
          <NavLink to="/education">EDUCATION</NavLink>
          <NavLink to="/contact">CONTACT</NavLink>
        </Navigation>

        <ImageContent>
          <ImageText
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Let's merge tradition and intelligence with 2 Taps
          </ImageText>
          <ButtonGroup>
            <Link to="/services">
              <Button primary large>What We Do</Button>
            </Link>
            <Link to="/education">
              <Button large>Dig Deeper</Button>
            </Link>
          </ButtonGroup>
        </ImageContent>

        <FeaturesSection>
          <Container>
            <SectionHeading>Our Secret Sauce</SectionHeading>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
            >
              <FeatureCards>
                {featureCards.map((feature, index) => (
                  <motion.div key={index} variants={fadeIn}>
                    <StyledFeatureCard>
                      <FeatureIcon>{feature.icon}</FeatureIcon>
                      <FeatureTitle>{feature.title}</FeatureTitle>
                      <FeatureDescription>
                        {feature.description}
                      </FeatureDescription>
                    </StyledFeatureCard>
                  </motion.div>
                ))}
              </FeatureCards>
            </motion.div>
          </Container>
        </FeaturesSection>

        <MissionSection>
          <Container>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeIn}>
                <MissionHeading>Our Mission</MissionHeading>
                <MissionText variants={fadeIn}>
                  Everyone is a builder. Imaginative architectures scattered throughout the world shape our shared story of humanity. Some are lost to time and cherished by history. Others stand proud and hold fast their roots for generations. Building is in our bones. At TapStructures, we will enhance the way humans build. We believe everyone should have the ability to create, to build, to construct. We hope to empower all those on their journey to explore what's possible. Crafted by hand, guided by intelligence.
                </MissionText>
              </motion.div>
            </motion.div>
          </Container>
        </MissionSection>

        <PortfolioSection>
          <Container>
            <PortfolioHeading
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Portfolio
            </PortfolioHeading>
            
            <PortfolioDescription
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Discover a captivating array of collections across a broad range of media. Some portfolios reveal our commitment to pioneering innovation, reliability, and quality in construction and others explore a world of architecture you may have never known. If you'd like to see your project showcased or have suggestions for generative media, send me a message over on the contact page.
            </PortfolioDescription>

            <PortfolioGrid
              albums={portfolioAlbums}
              onAlbumSelect={handleAlbumSelect}
            />

            <AlbumModal
              album={portfolioState.selectedAlbum}
              onClose={handleCloseModal}
              isLoading={portfolioState.isLoading}
            />
          </Container>
        </PortfolioSection>

        <CTASection>
          <Container>
            <CTAContent>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                <motion.div variants={fadeIn}>
                  <ImageHeading>Ready to Get Started?</ImageHeading>
                  <ImageText>
                    Hey, I'm Matt Tap! A builder wielding a passion for tackling complex problems with out-of-the-box solutions.
                    I'm currently working towards constructing a wider bridge between tradition and technology.
                    I see new practices taking off, high above the shoulders of giants, fueled by almost unimaginable technological advancements.
                    I'm determined to send shockwaves through the industry and rallying like-minded trailblazers to join me on this journey. Lets shape the future--together.
                  </ImageText>
                </motion.div>
                <motion.div variants={fadeIn}>
                  <Button
                    as={Link} 
                    to="/contact" 
                    primary
                    large
                  >
                    Book a Demo
                  </Button>
                </motion.div>
              </motion.div>
            </CTAContent>
          </Container>
        </CTASection>

        <BackToTopButton
          isVisible={showBackToTop}
          onClick={scrollToTop}
        />
      </PageContainer>
    </>
  );
};

export default LandingPage;